import { HsVector3 } from '../../math-new/objects/vector3.class';
import { HsGlobal } from '../global';
import type { HsCameraTool } from './camera-tool.interface';
import { HsQuaternion } from '../../math-new/objects/quaternion.class';
import type { HsCamera } from '../../engine-new/objects/cameras/camera.class';
import { HsMatrix4 } from '../../math-new/objects/matrix4.class';
import { HsOutputMessageObject } from '../messages/output-message-object.class';
import { HsOutputMessageType } from '../messages/output-message-type.enum';
import { hsApplyTransformationToCamera } from './apply-transformation-to-camera.function';
import { HsToolsConstants } from '../tools-constants.constants';
import { HsHypersceneMathUtils } from '../../math-new/functions/hyperscene-math-utils.class';

const TIME_CONVERSION_FACTOR: number = 500;

export class HsCameraFocusAt implements HsCameraTool {
  private readonly targetObjectPoint: HsVector3 = HsGlobal.ROTATE_POINT;

  private readonly cameraPosition: HsVector3 = HsVector3.makeZeroVector();

  private readonly focusPosition: HsVector3 = HsVector3.makeZeroVector();

  private readonly intermediatePosition: HsVector3 = HsVector3.makeZeroVector();

  private frameStartTime: number | null = null;

  private frameEndTime: number | null = null;

  private interpolationCoefficient: number = 0;

  private readonly startQuaternion: HsQuaternion = HsQuaternion.makeIdentityQuaternion();

  private readonly endQuaternion: HsQuaternion = HsQuaternion.makeIdentityQuaternion();

  private readonly currentQuaternion: HsQuaternion = HsQuaternion.makeIdentityQuaternion();

  static readonly #vector3Temp1: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp2: HsVector3 = HsVector3.makeZeroVector();
  static readonly #matrix4Temp1: HsMatrix4 = HsMatrix4.makeIdentityMatrix();

  constructor(private readonly camera: HsCamera) {}

  public processControlActions(): void {
    if (this.interpolationCoefficient === 0) {
      this.cameraPosition.setFromMatrixPosition(this.camera.worldMatrix.values);

      this.startQuaternion.setFromRotationMatrix(this.camera.worldMatrix.values);

      const target: HsVector3 = HsCameraFocusAt.#vector3Temp1.copy(this.targetObjectPoint);

      const upDirection: HsVector3 = HsCameraFocusAt.#vector3Temp2.set(0, 1, 0);
      const targetDirectionMatrix: HsMatrix4 = HsHypersceneMathUtils.lookAt(
        HsCameraFocusAt.#matrix4Temp1,
        this.cameraPosition,
        target,
        upDirection
      );
      this.endQuaternion.setFromRotationMatrix(targetDirectionMatrix.values);

      this.focusPosition
        .copy(target)
        .subtract(this.cameraPosition)
        .normalize()
        .negate()
        .multiplyByScalar(Math.abs(HsToolsConstants.FOCUS_POINT_DISTANCE))
        .add(this.targetObjectPoint);
    }

    if (this.interpolationCoefficient > 0 && this.interpolationCoefficient < 1) {
      this.intermediatePosition.setFromLerp(this.cameraPosition, this.focusPosition, this.interpolationCoefficient);
      this.currentQuaternion.slerp(this.startQuaternion, this.endQuaternion, this.interpolationCoefficient);
      const scale: HsVector3 = HsCameraFocusAt.#vector3Temp2.set(1, 1, 1);
      const newCameraMatrix: HsMatrix4 = HsCameraFocusAt.#matrix4Temp1.compose(
        this.intermediatePosition.values,
        this.currentQuaternion.values,
        scale.values
      );
      hsApplyTransformationToCamera(this.camera, newCameraMatrix);
    }

    if (this.interpolationCoefficient >= 1) {
      const scale: HsVector3 = HsCameraFocusAt.#vector3Temp2.set(1, 1, 1);
      const newCameraMatrix: HsMatrix4 = HsCameraFocusAt.#matrix4Temp1.compose(
        this.focusPosition.values,
        this.endQuaternion.values,
        scale.values
      );
      hsApplyTransformationToCamera(this.camera, newCameraMatrix);

      this.interpolationCoefficient = 0;
      this.frameStartTime = null;
      this.frameEndTime = null;

      this.sendOutputEvent();
      return;
    }

    this.frameStartTime = this.frameEndTime;
    this.frameEndTime = Date.now();
    const deltaTime: number = this.frameStartTime === null ? 1 : this.frameEndTime - this.frameStartTime;

    this.interpolationCoefficient += deltaTime / TIME_CONVERSION_FACTOR;
  }

  private sendOutputEvent(): void {
    const message: HsOutputMessageObject = new HsOutputMessageObject(HsOutputMessageType.CameraFocusAtComplete);
    HsGlobal.outputMessages.next(message);
  }
}
